import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { LogOut, LayoutDashboard, Users, Building2, Tag, Globe2, Cpu, ScrollText, ShieldCheck, ChevronRight, X, FileText, Download, Sparkles, Github, BookOpen, Search } from "lucide-react";

const TABS = [
  ["dashboard", "Dashboard", LayoutDashboard], ["candidates", "Candidates", Users],
  ["companies", "Companies", Building2], ["pricing", "Pricing", Tag],
  ["mobility", "Mobility", Globe2], ["ai", "AI Usage", Cpu],
  ["integrity", "Search Integrity", ShieldCheck], ["audit", "Audit", ScrollText],
];

export default function AdminPortal() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  return (
    <div className="min-h-screen">
      <header className="ta-glass border-b border-white/10 sticky top-0 z-40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><Logo /><span className="label-mono text-faint hidden sm:block">Admin</span></div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted hidden sm:block">{user?.email}</span>
            <button onClick={() => { logout(); nav("/"); }} className="text-muted hover:text-signal transition-colors duration-200"><LogOut size={18} /></button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-8">
        <div className="flex gap-2 overflow-x-auto border-b border-white/10 mb-8">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors duration-200 ${tab === k ? "border-azure text-ink" : "border-transparent text-muted hover:text-ink"}`}>
              <Icon size={15} /> {l}
            </button>
          ))}
        </div>
        {tab === "dashboard" && <Dashboard />}
        {tab === "candidates" && <Candidates />}
        {tab === "companies" && <Companies />}
        {tab === "pricing" && <PricingAdmin />}
        {tab === "mobility" && <MobilityAdmin />}
        {tab === "ai" && <AIUsage />}
        {tab === "integrity" && <SearchIntegrity />}
        {tab === "audit" && <Audit />}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, onClick, testId }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      {...(clickable ? { role: "button", tabIndex: 0, onClick,
        onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } } : {})}
      data-testid={testId}
      className={`group rounded-xl border border-white/10 bg-surface p-5 transition-colors duration-200 ${clickable ? "cursor-pointer hover:border-azure/50" : ""}`}>
      <div className="flex items-start justify-between">
        <p className={`font-display font-bold text-3xl ${accent || "text-ink"}`}>{value}</p>
        {clickable && <ChevronRight size={16} className="text-faint group-hover:text-azure transition-colors duration-200 mt-1" />}
      </div>
      <p className="label-mono text-faint mt-1">{label}</p>
    </div>
  );
}

function MetricModal({ metricKey, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/admin/metric/${metricKey}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ title: "Details", columns: [], rows: [], count: 0 }));
  }, [metricKey]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 sm:p-8 overflow-y-auto"
      data-testid="metric-modal" onClick={onClose}>
      <div className="w-full max-w-4xl rounded-2xl border border-white/10 bg-surface my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div>
            <p className="font-display font-bold text-xl text-ink" data-testid="metric-modal-title">{data ? data.title : "Loading…"}</p>
            {data && <p className="label-mono text-faint mt-0.5">{data.count} {data.count === 1 ? "record" : "records"}</p>}
          </div>
          <button onClick={onClose} data-testid="metric-modal-close" className="text-muted hover:text-ink transition-colors duration-200"><X size={20} /></button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
          {!data ? <p className="text-muted text-sm py-8 text-center">Loading…</p>
            : data.rows.length === 0 ? <p className="text-muted text-sm py-8 text-center">No records yet.</p>
            : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left label-mono text-faint">
                    {data.columns.map((c) => <th key={c} className="pb-3 pr-4 font-normal">{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => (
                    <tr key={i} data-testid={`metric-row-${i}`} className="border-t border-white/5">
                      {data.columns.map((c) => <td key={c} className="py-2.5 pr-4 text-ink align-top">{String(row[c] ?? "—")}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  const [metric, setMetric] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <p className="text-muted text-sm">Loading…</p>;
  const open = (k) => setMetric(k);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Candidates" value={d.candidates.total} testId="stat-candidates" onClick={() => open("candidates")} />
        <Stat label="Published" value={d.candidates.published} accent="text-emerald" testId="stat-published" onClick={() => open("published")} />
        <Stat label="Companies" value={d.companies.total} testId="stat-companies" onClick={() => open("companies")} />
        <Stat label="Approved companies" value={d.companies.approved} accent="text-azure" testId="stat-approved" onClick={() => open("approved")} />
        <Stat label="Annual active" value={d.companies.annual_active} testId="stat-annual_active" onClick={() => open("annual_active")} />
        <Stat label="Lifetime" value={d.companies.lifetime} accent="text-emerald" testId="stat-lifetime" onClick={() => open("lifetime")} />
        <Stat label="Expired" value={d.companies.expired} accent="text-signal" testId="stat-expired" onClick={() => open("expired")} />
        <Stat label="Revenue (USD)" value={`$${(d.revenue || 0).toLocaleString()}`} accent="text-emerald" testId="stat-revenue" onClick={() => open("revenue")} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Searches" value={d.activity.searches} testId="stat-searches" onClick={() => open("searches")} />
        <Stat label="Messages" value={d.activity.messages} testId="stat-messages" onClick={() => open("messages")} />
        <Stat label="Mobility cases" value={d.activity.mobility_cases} testId="stat-mobility" onClick={() => open("mobility")} />
        <Stat label="AI cost (USD)" value={`$${d.ai.estimated_cost_usd}`} accent="text-amber" testId="stat-ai_cost" onClick={() => open("ai_cost")} />
      </div>
      {metric && <MetricModal metricKey={metric} onClose={() => setMetric(null)} />}
    </div>
  );
}

function RiskChips({ flags }) {
  const active = Object.entries(flags || {}).filter(([, v]) => v).map(([k]) => k);
  if (!active.length) return null;
  const labels = { generic_email: "Generic email", website_email_domain_mismatch: "Domain mismatch", duplicate_company: "Possible duplicate" };
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="risk-chips">
      {active.map((k) => <span key={k} className="inline-flex items-center rounded-full bg-amber/15 px-2.5 py-0.5 text-[11px] text-amber">⚠ {labels[k] || k}</span>)}
    </div>
  );
}

function ActionBtn({ onClick, children, tone = "default", testId }) {
  const tones = { default: "border-white/15 text-muted hover:text-ink", ok: "border-emerald/30 text-emerald hover:bg-emerald/10", bad: "border-signal/30 text-signal hover:bg-signal/10", blue: "bg-azure text-white hover:bg-white hover:text-black border-transparent" };
  return <button onClick={onClick} data-testid={testId} className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ${tones[tone]}`}>{children}</button>;
}

function Row({ k, v }) {
  if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) return null;
  return <div className="flex gap-3 py-1.5 border-b border-white/5 text-sm"><span className="text-faint w-44 shrink-0">{k}</span><span className="text-ink break-words">{Array.isArray(v) ? v.join(", ") : String(v)}</span></div>;
}

const CAND_GROUPS = [
  { key: "new", label: "New", statuses: ["submitted", "under_review", "info_requested"] },
  { key: "approved", label: "Approved", statuses: ["approved", "approved_with_conditions"] },
  { key: "waitlist", label: "Waitlist", statuses: ["waitlisted"] },
  { key: "rejected", label: "Rejected", statuses: ["rejected"] },
  { key: "drafts", label: "Drafts", statuses: ["draft"] },
  { key: "inactive", label: "Suspended / Archived", statuses: ["suspended", "archived"] },
  { key: "all", label: "All", statuses: null },
];

function Candidates() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const [group, setGroup] = useState("new");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const load = () => api.get("/admin/candidates").then((r) => setList(r.data.candidates)).catch(() => {});
  useEffect(() => { load(); }, []);
  const countFor = (g) => (g.statuses ? list.filter((c) => g.statuses.includes(c.status)).length : list.length);
  const active = CAND_GROUPS.find((g) => g.key === group) || CAND_GROUPS[0];
  const dateOf = (c) => c.submitted_at || c.created_at || "";
  const q = query.trim().toLowerCase();
  const visible = (active.statuses ? list.filter((c) => active.statuses.includes(c.status)) : list)
    .filter((c) => !q || [c.legal_name, c.first_name, c.last_name, c.email, c.headline, c.primary_role]
      .some((f) => (f || "").toLowerCase().includes(q)))
    .sort((a, b) => {
      if (sortBy === "completeness") return (b.completeness || 0) - (a.completeness || 0);
      if (sortBy === "date_asc") return String(dateOf(a)).localeCompare(String(dateOf(b)));
      return String(dateOf(b)).localeCompare(String(dateOf(a)));
    });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="candidate-tabs">
        {CAND_GROUPS.map((g) => (
          <button key={g.key} onClick={() => { setGroup(g.key); setOpen(null); }} data-testid={`cand-tab-${g.key}`}
            className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors duration-200 ${group === g.key ? "bg-azure text-white" : "border border-white/15 text-muted hover:text-ink"}`}>
            {g.label} <span className="ml-1 opacity-70">{countFor(g)}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-faint" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email or role…" data-testid="cand-search"
            className="w-full rounded-lg border border-white/15 bg-surface pl-9 pr-3 py-2 text-sm text-ink focus:border-azure focus:outline-none" />
        </div>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="cand-sort"
          className="rounded-lg border border-white/15 bg-surface px-3 py-2 text-xs text-ink focus:border-azure focus:outline-none">
          <option value="date_desc">Date applied · newest</option>
          <option value="date_asc">Date applied · oldest</option>
          <option value="completeness">Completeness · high to low</option>
        </select>
        <span className="text-xs text-faint" data-testid="cand-result-count">{visible.length} shown</span>
      </div>
      <div className="space-y-3">
        {visible.length === 0 && <p className="text-sm text-faint py-4" data-testid="cand-empty">No candidates match.</p>}
        {visible.map((c) => {
          const needsPublish = ["approved", "approved_with_conditions"].includes(c.status) && !c.published;
          return (
            <div key={c.id} className="rounded-xl border border-white/10 bg-surface p-5" data-testid={`admin-candidate-${c.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-display font-bold text-ink flex items-center gap-2 flex-wrap">
                    {c.legal_name || c.email} {c.is_seed && <span className="text-[10px] text-faint">(seed)</span>}
                    {needsPublish && <span className="rounded-full bg-amber/15 px-2 py-0.5 text-[10px] text-amber" data-testid={`not-published-${c.id}`}>not published — hidden from search</span>}
                  </p>
                  <p className="text-sm text-muted">{c.headline || c.primary_role || "—"}</p>
                  <p className="text-xs text-faint font-mono mt-1">status: {c.status} · complete: {c.completeness ?? 0}% · visibility: {c.visibility} · published: {String(c.published)}</p>
                </div>
                <ActionBtn onClick={() => setOpen(open === c.id ? null : c.id)} testId={`review-cand-${c.id}`}>{open === c.id ? "Close" : "Review"}</ActionBtn>
              </div>
              {open === c.id && <CandidateReview id={c.id} reload={load} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CandidateReview({ id, reload }) {
  const [d, setD] = useState(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const [showResume, setShowResume] = useState(false);
  const load = () => api.get(`/admin/candidates/${id}`).then((r) => setD(r.data.candidate)).catch(() => {});
  useEffect(() => { load(); }, [id]);
  const act = async (body) => {
    try { await api.put(`/admin/candidates/${id}`, body); toast.success("Updated"); setReason(""); setNote(""); setMsg(""); load(); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const importEv = async (source) => {
    try {
      const { data } = await api.post(`/admin/candidates/${id}/import-evidence`, { source });
      toast.success(`Imported ${data.added} new ${source === "github" ? "projects" : "publications"} (${data.fetched} found)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!d) return <p className="text-muted text-sm mt-4">Loading…</p>;
  const v = d.validation || {};
  return (
    <div className="mt-4 border-t border-white/10 pt-4 space-y-4" data-testid={`candidate-review-${id}`}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border border-white/10 p-3"><p className="font-display font-bold text-xl">{v.completeness ?? 0}%</p><p className="text-[11px] text-faint">complete</p></div>
        <div className="rounded-lg border border-white/10 p-3"><p className={`font-display font-bold text-xl ${v.blocking_errors?.length ? "text-signal" : "text-emerald"}`}>{v.blocking_errors?.length || 0}</p><p className="text-[11px] text-faint">blocking issues</p></div>
        <div className="rounded-lg border border-white/10 p-3"><p className="font-display font-bold text-xl">{(d.references || []).filter((r) => r.status === "received").length}</p><p className="text-[11px] text-faint">refs received</p></div>
        <div className="rounded-lg border border-white/10 p-3"><p className={`font-display font-bold text-xl ${v.duplicate_email ? "text-amber" : "text-ink"}`}>{v.duplicate_email ? "Yes" : "No"}</p><p className="text-[11px] text-faint">dup email</p></div>
      </div>
      {v.blocking_errors?.length > 0 && <div className="rounded-lg border border-signal/20 bg-signal/5 px-4 py-2 text-xs text-signal" data-testid="cand-blocking">Blocks approval: {v.blocking_errors.join("; ")}</div>}
      <div>
        <Row k="Name" v={[d.first_name, d.last_name].filter(Boolean).join(" ")} />
        <Row k="Email" v={d.email} /><Row k="Country" v={d.country} /><Row k="LinkedIn" v={d.linkedin} />
        <Row k="Headline" v={d.headline} /><Row k="Primary role" v={d.primary_role} /><Row k="Seniority" v={d.seniority} />
        <Row k="Experience" v={d.years_experience} /><Row k="English" v={d.english_proficiency} />
        <Row k="Work arrangement" v={d.work_arrangement} /><Row k="US locations" v={d.preferred_us_locations} />
        <Row k="Skills" v={(d.skills || []).map((s) => `${s.name} (${s.proficiency})`)} />
        <Row k="Résumé" v={d.resume_uploaded ? "uploaded" : "—"} />
      </div>
      {(d.resume_storage_path || d.resume_text) ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="resume-actions">
          <button onClick={() => setShowResume(true)} data-testid="view-resume-btn"
            className="inline-flex items-center gap-2 rounded-full bg-azure px-4 py-2 text-xs font-medium text-white hover:bg-white hover:text-black transition-colors duration-200">
            <FileText size={14} /> View résumé{d.resume_filename ? ` · ${d.resume_filename}` : ""}
          </button>
        </div>
      ) : (
        <p className="text-xs text-faint" data-testid="resume-none">No résumé on file.</p>
      )}
      {showResume && <ResumeViewer candidateId={id} filename={d.resume_filename} hasFile={!!d.resume_storage_path} resumeText={d.resume_text} resumeParsed={d.resume_parsed} onClose={() => setShowResume(false)} />}
      {(d.github || d.orcid) && (
        <div className="flex flex-wrap items-center gap-2" data-testid="evidence-import">
          <span className="label-mono text-faint">Auto-fill evidence:</span>
          {d.github && <button onClick={() => importEv("github")} data-testid="admin-import-github" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-ink hover:bg-white/5 transition-colors duration-200"><Github size={13} /> Import GitHub</button>}
          {d.orcid && <button onClick={() => importEv("orcid")} data-testid="admin-import-orcid" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-ink hover:bg-white/5 transition-colors duration-200"><BookOpen size={13} /> Import ORCID</button>}
        </div>
      )}
      {(d.projects?.length > 0 || d.publications?.length > 0) && (
        <div className="rounded-lg border border-white/10 p-4 space-y-3" data-testid="evidence-panel">
          {d.projects?.length > 0 && (
            <div data-testid="evidence-projects">
              <p className="label-mono text-faint mb-1.5">Projects ({d.projects.length})</p>
              <div className="space-y-1.5">
                {d.projects.slice(0, 12).map((p, i) => (
                  <div key={i} className="text-xs">
                    {p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="text-azure hover:underline">{p.title}</a> : <span className="text-ink">{p.title}</span>}
                    {p.source === "github" && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-faint"><Github size={10} /> GitHub</span>}
                    {p.impact && <span className="ml-2 text-faint">· {p.impact}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {d.publications?.length > 0 && (
            <div data-testid="evidence-publications">
              <p className="label-mono text-faint mb-1.5">Publications ({d.publications.length})</p>
              <ul className="space-y-1.5">
                {d.publications.slice(0, 15).map((p, i) => (
                  <li key={i} className="text-xs">
                    {p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="text-azure hover:underline">{p.title || String(p)}</a> : <span className="text-ink">{p.title || String(p)}</span>}
                    {p.year && <span className="ml-2 text-faint">({p.year})</span>}
                    {p.source === "orcid" && <span className="ml-2 text-[10px] text-faint">ORCID</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (optional)" data-testid="cand-note" className="w-full rounded-lg border border-white/12 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none" />
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required for reject / override)" data-testid="cand-reason" className="w-full rounded-lg border border-white/12 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none" />
      <textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Message to candidate (optional — included in the email they receive)" data-testid="cand-message" className="w-full rounded-lg border border-azure/25 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none" />
      <div className="flex flex-wrap gap-2">
        <ActionBtn tone="ok" testId={`cand-approve-${id}`} onClick={() => act({ status: "approved", vetting_stage: "approved", override_reason: reason || undefined, note: note || undefined, message_to_candidate: msg || undefined })}>Approve</ActionBtn>
        <ActionBtn testId={`cand-conditions-${id}`} onClick={() => act({ status: "approved_with_conditions", conditions: reason || undefined, override_reason: reason || undefined, message_to_candidate: msg || undefined })}>Approve w/ conditions</ActionBtn>
        <ActionBtn tone="blue" testId={`cand-publish-${id}`} onClick={() => act({ published: true })}>Publish</ActionBtn>
        <ActionBtn testId={`cand-unpublish-${id}`} onClick={() => act({ published: false, visibility: "hidden" })}>Unpublish</ActionBtn>
        <ActionBtn testId={`cand-waitlist-${id}`} onClick={() => act({ status: "waitlisted", message_to_candidate: msg || undefined })}>Waitlist</ActionBtn>
        <ActionBtn testId={`cand-info-${id}`} onClick={() => act({ status: "info_requested", note: note || undefined, message_to_candidate: msg || undefined })}>Request info</ActionBtn>
        <ActionBtn tone="bad" testId={`cand-reject-${id}`} onClick={() => act({ status: "rejected", rejection_reason: reason, message_to_candidate: msg || undefined })}>Reject</ActionBtn>
        <ActionBtn testId={`cand-suspend-${id}`} onClick={() => act({ status: "suspended" })}>Suspend</ActionBtn>
        <ActionBtn testId={`cand-archive-${id}`} onClick={() => act({ status: "archived" })}>Archive</ActionBtn>
        <ActionBtn testId={`cand-note-btn-${id}`} onClick={() => (note.trim() ? act({ note }) : toast.error("Type a note in the box above first"))}>Add note</ActionBtn>
      </div>
      {d.admin_notes?.length > 0 && (
        <div className="rounded-lg border border-white/10 p-4" data-testid="admin-notes">
          <p className="label-mono text-faint mb-3">Internal notes</p>
          <div className="space-y-2">
            {[...d.admin_notes].reverse().map((n, i) => (
              <div key={i} className="border-l-2 border-white/15 pl-3" data-testid={`admin-note-${i}`}>
                <p className="text-sm text-ink whitespace-pre-wrap">{n.note}</p>
                <p className="text-[11px] text-faint mt-0.5">{n.by} · {new Date(n.at).toLocaleString("en-US")}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <CommHistory items={d.communications} />
      <AuditList logs={d.audit} />
    </div>
  );
}

function CommHistory({ items, recipientLabel = "candidate" }) {
  if (!items?.length) return null;
  const labels = { info_requested: "Requested more info", approved: "Approved", approved_with_conditions: "Approved w/ conditions", rejected: "Rejected", waitlisted: "Waitlisted" };
  const statusColor = { sent: "text-emerald", failed: "text-signal", no_email: "text-amber" };
  const statusLabel = { sent: "delivered", failed: "send failed", no_email: "no email on file" };
  return (
    <div className="rounded-lg border border-white/10 p-4" data-testid="comm-history">
      <p className="label-mono text-faint mb-3">Communication history · emails sent to {recipientLabel}</p>
      <div className="space-y-3">
        {items.map((m) => (
          <div key={m.id} className="border-l-2 border-azure/40 pl-3" data-testid={`comm-${m.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-ink font-medium">{labels[m.type] || m.type}</span>
              <span className={`text-[10px] uppercase font-mono ${statusColor[m.status] || "text-faint"}`}>{statusLabel[m.status] || m.status}</span>
              <span className="text-[11px] text-faint">{new Date(m.created_at).toLocaleString("en-US")}</span>
            </div>
            <p className="text-xs text-muted mt-0.5">"{m.subject}" → {m.to || "—"} · by {m.sent_by}</p>
            {m.message && <p className="text-xs text-ink/80 mt-1 italic border-l-2 border-white/15 pl-2" data-testid={`comm-msg-${m.id}`}>"{m.message}"</p>}
            {m.missing_fields?.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={`comm-missing-${m.id}`}>
                {m.missing_fields.map((f, i) => <span key={i} className="inline-flex rounded-full bg-amber/10 px-2 py-0.5 text-[10px] text-amber">{f}</span>)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ResumeViewer({ candidateId, filename, hasFile, resumeText, resumeParsed, onClose }) {
  const [url, setUrl] = useState(null);
  const [ct, setCt] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(hasFile);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!hasFile) return;
    let objUrl;
    api.get("/candidate/resume/download", { params: { candidate_id: candidateId }, responseType: "blob" })
      .then((res) => { setCt(res.data.type || ""); objUrl = URL.createObjectURL(res.data); setUrl(objUrl); })
      .catch(() => setErr("Could not load the résumé file."))
      .finally(() => setLoading(false));
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [candidateId, hasFile]);

  const isPdf = (ct || "").includes("pdf") || (filename || "").toLowerCase().endsWith(".pdf");
  const isText = (ct || "").startsWith("text/") || /\.(txt|md|csv)$/i.test(filename || "");
  const isImage = (ct || "").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(filename || "");
  const canInline = isPdf || isText || isImage;
  const download = () => { if (url) { const a = document.createElement("a"); a.href = url; a.download = filename || "resume"; a.click(); } };

  const p = resumeParsed || {};
  const tech = (t) => (Array.isArray(t) ? t.join(", ") : (t || ""));
  const hasHighlights = !!(p.primary_role || p.seniority || (p.skills || []).length || (p.employment || []).length || (p.projects || []).length || (p.achievements || []).length);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="resume-modal">
      <div className="w-full max-w-6xl h-[85vh] flex flex-col rounded-xl border border-white/12 bg-surface overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-3">
          <p className="font-display font-bold text-ink flex items-center gap-2 truncate"><FileText size={16} className="text-azure shrink-0" /> {filename || "Résumé"}</p>
          <div className="flex items-center gap-2">
            {hasFile && url && <button onClick={download} data-testid="resume-download-btn" className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-ink hover:bg-white/5 transition-colors duration-200"><Download size={13} /> Download</button>}
            <button onClick={onClose} data-testid="resume-close-btn" className="rounded-full border border-white/15 p-1.5 text-muted hover:text-ink transition-colors duration-200"><X size={16} /></button>
          </div>
        </div>
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          <div className="flex-1 min-h-0 bg-base">
            {loading && <p className="text-muted text-sm p-6">Loading résumé…</p>}
            {err && <p className="text-signal text-sm p-6">{err}</p>}
            {!loading && !err && hasFile && canInline && url && (
              <iframe title="résumé" src={url} className="w-full h-full border-0 bg-white" data-testid="resume-iframe" />
            )}
            {!loading && !err && hasFile && !canInline && (
              <div className="p-6 text-sm text-muted">
                <p>Inline preview isn't available for this file type.</p>
                <button onClick={download} className="mt-3 inline-flex items-center gap-2 rounded-full bg-azure px-4 py-2 text-xs font-medium text-white hover:bg-white hover:text-black transition-colors duration-200"><Download size={14} /> Download {filename}</button>
              </div>
            )}
            {!hasFile && resumeText && (
              <pre className="whitespace-pre-wrap break-words p-6 text-sm text-ink font-mono leading-relaxed overflow-auto h-full" data-testid="resume-text">{resumeText}</pre>
            )}
            {!hasFile && !resumeText && !loading && <p className="text-faint text-sm p-6">No résumé content on file.</p>}
          </div>
          {hasHighlights && (
            <aside className="lg:w-80 shrink-0 overflow-auto border-t lg:border-t-0 lg:border-l border-white/10 bg-surface p-5 space-y-4" data-testid="resume-highlights">
              <div>
                <p className="label-mono text-azure flex items-center gap-1.5"><Sparkles size={13} /> AI-parsed highlights</p>
                <p className="text-[11px] text-faint mt-1">Extracted from the résumé — verify against the document.</p>
              </div>
              {(p.primary_role || p.seniority || p.years_experience > 0) && (
                <div className="space-y-1" data-testid="hl-role">
                  {p.primary_role && <p className="text-sm text-ink font-medium">{p.primary_role}</p>}
                  <p className="text-xs text-muted">{[p.seniority, p.years_experience > 0 ? `${p.years_experience} yrs` : null].filter(Boolean).join(" · ")}</p>
                </div>
              )}
              {(p.skills || []).length > 0 && (
                <div data-testid="hl-skills">
                  <p className="label-mono text-faint mb-1.5">Skills</p>
                  <div className="flex flex-wrap gap-1.5">
                    {p.skills.slice(0, 30).map((s, i) => <span key={i} className="inline-flex rounded-full bg-azure/10 px-2 py-0.5 text-[11px] text-azure">{s}</span>)}
                  </div>
                </div>
              )}
              {(p.employment || []).length > 0 && (
                <div data-testid="hl-employment">
                  <p className="label-mono text-faint mb-1.5">Experience</p>
                  <div className="space-y-2">
                    {p.employment.map((e, i) => (
                      <div key={i} className="border-l-2 border-white/15 pl-2.5">
                        <p className="text-xs text-ink font-medium">{e.title || "—"}{e.company ? ` · ${e.company}` : ""}</p>
                        {e.dates && <p className="text-[11px] text-faint font-mono">{e.dates}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(p.projects || []).length > 0 && (
                <div data-testid="hl-projects">
                  <p className="label-mono text-faint mb-1.5">Projects</p>
                  <div className="space-y-2">
                    {p.projects.map((pr, i) => (
                      <div key={i}>
                        <p className="text-xs text-ink font-medium">{pr.title || "—"}</p>
                        {tech(pr.technologies) && <p className="text-[11px] text-muted">{tech(pr.technologies)}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(p.achievements || []).length > 0 && (
                <div data-testid="hl-achievements">
                  <p className="label-mono text-faint mb-1.5">Achievements</p>
                  <ul className="list-disc pl-4 space-y-1">
                    {p.achievements.map((a, i) => <li key={i} className="text-[11px] text-muted leading-relaxed">{a}</li>)}
                  </ul>
                </div>
              )}
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}

function AuditList({ logs }) {
  if (!logs?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 p-3">
      <p className="label-mono text-faint mb-2">History</p>
      <div className="space-y-1 font-mono text-[11px]">
        {logs.slice(0, 12).map((l) => (
          <div key={l.id} className="flex gap-3 text-muted"><span className="text-faint">{new Date(l.created_at).toLocaleString("en-US")}</span><span className="text-azure">{l.action}</span><span className="truncate">{l.actor_role}</span></div>
        ))}
      </div>
    </div>
  );
}

function companyAttention(c) {
  let s = 0;
  if (["submitted", "under_review", "info_requested"].includes(c.approval_status)) s += 3;
  s += Object.values(c.risk_flags || {}).filter(Boolean).length * 2;
  if ((c.completeness ?? 100) < 60) s += 2;
  if (["approved", "rejected", "archived", "suspended"].includes(c.approval_status)) s -= 5;
  return s;
}

function Companies() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const [naFirst, setNaFirst] = useState(true);
  const load = () => api.get("/admin/companies").then((r) => setList(r.data.companies)).catch(() => {});
  useEffect(() => { load(); }, []);
  const rows = naFirst ? [...list].sort((a, b) => companyAttention(b) - companyAttention(a)) : list;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="label-mono text-faint">{list.length} companies</p>
        <button onClick={() => setNaFirst(!naFirst)} data-testid="needs-attention-toggle"
          className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors duration-200 ${naFirst ? "border-amber/40 bg-amber/10 text-amber" : "border-white/15 text-muted hover:text-ink"}`}>
          {naFirst ? "⚠ Needs attention first" : "Sort: default"}
        </button>
      </div>
      {rows.map((c) => (
        <div key={c.id} className="rounded-xl border border-white/10 bg-surface p-5" data-testid={`admin-company-${c.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="font-display font-bold text-ink flex items-center gap-2">
                {c.legal_name || "—"} {c.is_seed && <span className="text-[10px] text-faint">(seed)</span>}
                {companyAttention(c) >= 3 && <span className="inline-flex items-center rounded-full bg-amber/15 px-2 py-0.5 text-[10px] text-amber" data-testid={`attention-${c.id}`}>⚠ needs attention</span>}
              </p>
              <p className="text-xs text-faint font-mono mt-1">approval: {c.approval_status} · access: {c.access?.status} · complete: {c.completeness ?? "—"}%</p>
              <RiskChips flags={c.risk_flags} />
            </div>
            <ActionBtn onClick={() => setOpen(open === c.id ? null : c.id)} testId={`review-company-${c.id}`}>{open === c.id ? "Close" : "Review"}</ActionBtn>
          </div>
          {open === c.id && <CompanyReview id={c.id} reload={load} />}
        </div>
      ))}
    </div>
  );
}

function CompanyReview({ id, reload }) {
  const [d, setD] = useState(null);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [msg, setMsg] = useState("");
  const load = () => api.get(`/admin/companies/${id}`).then((r) => setD(r.data.company)).catch(() => {});
  useEffect(() => { load(); }, [id]);
  const act = async (body) => {
    try { await api.put(`/admin/companies/${id}`, body); toast.success("Updated"); setReason(""); setNote(""); setMsg(""); load(); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const grant = async (plan) => { try { await api.post(`/admin/companies/${id}/grant-access`, { plan, source: "complimentary" }); toast.success(`Granted ${plan}`); load(); reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  if (!d) return <p className="text-muted text-sm mt-4">Loading…</p>;
  return (
    <div className="mt-4 border-t border-white/10 pt-4 space-y-4" data-testid={`company-review-${id}`}>
      <RiskChips flags={d.risk_flags} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
        <div>
          <Row k="Trading name" v={d.trading_name} /><Row k="Website" v={d.website} /><Row k="LinkedIn" v={d.linkedin_company} />
          <Row k="HQ" v={d.headquarters} /><Row k="Year founded" v={d.year_founded} /><Row k="Employees" v={d.employees} />
          <Row k="Industry" v={d.industry} /><Row k="Funding stage" v={d.funding_stage} /><Row k="Capital raised" v={d.capital_raised} />
          <Row k="Investors" v={d.investors} /><Row k="Accelerator" v={d.accelerator} /><Row k="YC batch" v={d.yc_batch} />
        </div>
        <div>
          <Row k="Tech stack" v={d.tech_stack} /><Row k="Target roles" v={d.target_roles} /><Row k="Hiring volume" v={d.expected_hiring_volume} />
          <Row k="Timeline" v={d.hiring_timeline} /><Row k="Work arrangement" v={d.work_arrangement} /><Row k="US locations" v={d.us_locations} />
          <Row k="Intl hiring" v={d.international_hiring} /><Row k="Relocation" v={d.relocation_support ? "Yes" : "No"} /><Row k="Visa" v={d.visa_sponsorship ? "Yes" : "No"} />
          <Row k="Representative" v={`${d.rep_first_name || ""} ${d.rep_last_name || ""} — ${d.rep_title || ""}`} /><Row k="Work email" v={d.rep_email} /><Row k="Rep LinkedIn" v={d.rep_linkedin} />
          <Row k="Terms accepted" v={d.agreements_accepted ? "Yes" : "No"} />
        </div>
      </div>
      <Row k="Company desc" v={d.company_description} /><Row k="Product desc" v={d.product_description} />
      {d.rejection_reason && <div className="rounded-lg border border-signal/20 bg-signal/5 px-4 py-2 text-xs text-signal">Rejection reason: {d.rejection_reason}</div>}
      <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (optional)" data-testid="co-note" className="w-full rounded-lg border border-white/12 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none" />
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Rejection reason (required to reject)" data-testid="co-reason" className="w-full rounded-lg border border-white/12 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none" />
      <textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Message to company (optional — included in the email they receive)" data-testid="co-message" className="w-full rounded-lg border border-azure/25 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none" />
      <div className="flex flex-wrap gap-2">
        <ActionBtn tone="ok" testId={`co-approve-${id}`} onClick={() => act({ approval_status: "approved", note: note || undefined, message_to_company: msg || undefined })}>Approve</ActionBtn>
        <ActionBtn testId={`co-review-${id}`} onClick={() => act({ approval_status: "under_review" })}>Under review</ActionBtn>
        <ActionBtn testId={`co-info-${id}`} onClick={() => act({ approval_status: "info_requested", note: note || undefined, message_to_company: msg || undefined })}>Request info</ActionBtn>
        <ActionBtn tone="bad" testId={`co-reject-${id}`} onClick={() => act({ approval_status: "rejected", rejection_reason: reason, message_to_company: msg || undefined })}>Reject</ActionBtn>
        <ActionBtn testId={`co-suspend-${id}`} onClick={() => act({ approval_status: "suspended" })}>Suspend</ActionBtn>
        <ActionBtn testId={`co-archive-${id}`} onClick={() => act({ approval_status: "archived" })}>Archive</ActionBtn>
        <ActionBtn onClick={() => grant("annual")}>Grant annual</ActionBtn>
        <ActionBtn onClick={() => grant("lifetime")}>Grant lifetime</ActionBtn>
        <ActionBtn testId={`co-note-btn-${id}`} onClick={() => note && act({ note })}>Add note</ActionBtn>
      </div>
      <CommHistory items={d.communications} recipientLabel="company" />
      <AuditList logs={d.audit} />
    </div>
  );
}

function PricingAdmin() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/admin/pricing").then((r) => setP(r.data)).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/admin/pricing", { data: p }); toast.success("Pricing updated"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  if (!p) return <p className="text-muted text-sm">Loading…</p>;
  const setPlan = (k, field, v) => setP({ ...p, plans: { ...p.plans, [k]: { ...p.plans[k], [field]: v } } });
  const input = "w-full rounded-lg border border-white/12 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none";
  return (
    <div className="max-w-3xl space-y-5">
      <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" className="h-4 w-4 accent-azure" checked={p.public_pricing_visible} onChange={(e) => setP({ ...p, public_pricing_visible: e.target.checked })} data-testid="pricing-visible" /> Show public pricing</label>
      {["annual", "lifetime"].map((k) => (
        <div key={k} className="rounded-xl border border-white/10 bg-surface p-5 space-y-3" data-testid={`pricing-plan-${k}`}>
          <p className="label-mono text-azure">{k} plan</p>
          <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" className="h-4 w-4 accent-azure" checked={p.plans[k].active} onChange={(e) => setPlan(k, "active", e.target.checked)} data-testid={`plan-active-${k}`} /> Active</label>
          <label className="flex items-center gap-2 text-sm text-muted"><input type="checkbox" className="h-4 w-4 accent-azure" checked={p.plans[k].recommended} onChange={(e) => setPlan(k, "recommended", e.target.checked)} /> Recommended</label>
          <input className={input} value={p.plans[k].price_display} onChange={(e) => setPlan(k, "price_display", e.target.value)} placeholder="Price display" />
          <input className={input} value={p.plans[k].description} onChange={(e) => setPlan(k, "description", e.target.value)} placeholder="Description" />
          <input className={input} value={p.plans[k].button_text} onChange={(e) => setPlan(k, "button_text", e.target.value)} placeholder="Button text" />
        </div>
      ))}
      <div className="rounded-xl border border-white/10 bg-surface p-5 space-y-3">
        <p className="label-mono text-azure">Grace period & upgrade</p>
        <input type="number" className={input} value={p.grace_period_days} onChange={(e) => setP({ ...p, grace_period_days: Number(e.target.value) })} placeholder="Grace days" />
        <input type="number" className={input} value={p.upgrade.credit} onChange={(e) => setP({ ...p, upgrade: { ...p.upgrade, credit: Number(e.target.value) } })} placeholder="Upgrade credit" />
      </div>
      <button onClick={save} data-testid="save-pricing-btn" className="rounded-full bg-azure px-6 py-3 text-sm font-medium text-white hover:bg-white hover:text-black transition-colors duration-200">Save pricing configuration</button>
    </div>
  );
}

function MobilityAdmin() {
  const [cases, setCases] = useState([]);
  const load = () => api.get("/mobility/admin/cases").then((r) => setCases(r.data.cases)).catch(() => {});
  useEffect(() => { load(); }, []);
  const STATUSES = ["interest_submitted", "preliminary_review", "referred_to_counsel", "referred_to_sponsor", "program_design", "consular_stage", "approved", "candidate_arrived", "completed", "closed"];
  const upd = async (id, status) => { try { await api.put(`/mobility/admin/cases/${id}`, { status }); toast.success("Updated"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <div className="space-y-3">
      {cases.length === 0 && <p className="text-muted text-sm">No mobility cases yet.</p>}
      {cases.map((c) => (
        <div key={c.id} className="rounded-xl border border-white/10 bg-surface p-5 flex items-center justify-between gap-4 flex-wrap">
          <div><p className="font-display font-bold text-ink">{c.proposed_role} — {c.proposed_location}</p><p className="text-xs text-faint font-mono mt-1">{(c.status || "").replace(/_/g, " ")}</p></div>
          <select value={c.status} onChange={(e) => upd(c.id, e.target.value)} className="rounded-lg border border-white/12 bg-base px-3 py-2 text-sm text-ink focus:border-azure focus:outline-none">
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
}

function AIUsage() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/ai-usage").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <p className="text-muted text-sm">Loading…</p>;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="AI requests" value={d.total_requests} />
        <Stat label="Tokens used" value={(d.total_tokens || 0).toLocaleString()} accent="text-azure" />
        <Stat label="Est. cost (USD)" value={`$${d.total_cost_usd}`} accent="text-amber" />
        <Stat label="Task types" value={Object.keys(d.by_task).length} />
      </div>
      <p className="text-xs text-faint" data-testid="ai-usage-note">
        Token counts are the <span className="text-ink">actual</span> usage reported by the model provider
        ({d.actual_token_requests}/{d.total_requests} requests). Cost is an estimate (tokens × approximate per‑token rate) —
        for exact billing see your Emergent Universal Key balance.
      </p>
      <div className="rounded-xl border border-white/10 bg-surface p-5">
        <p className="label-mono text-faint mb-3">Usage by task</p>
        {Object.entries(d.by_task).map(([t, v]) => (
          <div key={t} className="flex justify-between py-2 border-b border-white/5 text-sm">
            <span className="text-muted">{t}</span><span className="font-mono text-ink">{v.count} req · {(v.tokens || 0).toLocaleString()} tok · ${v.cost.toFixed(4)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Audit() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setLogs(r.data.logs)).catch(() => {}); }, []);
  return (
    <div className="rounded-xl border border-white/10 bg-surface p-5">
      <p className="label-mono text-faint mb-3">Recent audit events</p>
      <div className="space-y-1.5 font-mono text-xs">
        {logs.map((l) => (
          <div key={l.id} className="flex gap-3 text-muted py-1 border-b border-white/5">
            <span className="text-faint">{new Date(l.created_at).toLocaleString("en-US")}</span>
            <span className="text-azure">{l.action}</span>
            <span className="text-faint">{l.entity}</span>
            <span className="truncate">{l.actor_role}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function SearchIntegrity() {
  const [diags, setDiags] = useState([]);
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/admin/search-diagnostics?limit=50").then((r) => setDiags(r.data.diagnostics)).catch(() => {});
  useEffect(() => { load(); }, []);
  const open = async (id) => {
    try { const { data } = await api.get(`/admin/search-diagnostics/${id}`); setDetail({ ...data.diagnostic, _names: data.candidate_names }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const name = (id) => (detail?._names?.[id]) || id;

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="search-integrity">
      <div className="rounded-xl border border-white/10 bg-surface p-5">
        <p className="label-mono text-faint mb-3">Recent searches — mandatory-matching diagnostics</p>
        <div className="space-y-1.5">
          {diags.length === 0 && <p className="text-sm text-muted">No searches run yet.</p>}
          {diags.map((d) => (
            <button key={d.id} onClick={() => open(d.id)} data-testid={`diag-${d.id}`}
              className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors duration-200 ${detail?.id === d.id ? "border-azure bg-azure/5" : "border-white/10 hover:bg-white/5"}`}>
              <div className="flex justify-between items-center">
                <span className="text-xs font-mono text-faint">{new Date(d.created_at).toLocaleString("en-US")}</span>
                <span className={`text-[10px] rounded-full px-2 py-0.5 ${d.final > 0 ? "bg-emerald/10 text-emerald" : "bg-signal/10 text-signal"}`}>{d.final} matched</span>
              </div>
              <p className="text-sm text-ink mt-1">{(d.mandatory?.required_skills || []).join(", ") || "—"}{d.mandatory?.min_years ? ` · ${d.mandatory.min_years}+ yrs` : ""}</p>
              <p className="text-[11px] text-faint font-mono mt-0.5">considered {d.considered} · eligible {d.eligible} · excluded {d.excluded} · strict {String(d.strict)}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-surface p-5">
        {!detail ? <p className="text-sm text-muted">Select a search to see why each candidate qualified, was excluded, or is adjacent.</p> : (
          <div data-testid="diag-detail">
            <p className="label-mono text-faint mb-2">Mandatory criteria</p>
            <pre className="text-[11px] text-muted bg-base/50 rounded-lg p-3 overflow-x-auto mb-4">{JSON.stringify(detail.mandatory, null, 2)}</pre>

            <p className="label-mono text-emerald mb-2">Qualified ({detail.eligible_ids?.length || 0})</p>
            <div className="space-y-1.5 mb-4">
              {(detail.eligible_ids || []).map((id) => (
                <div key={id} className="rounded-lg border border-emerald/20 bg-emerald/5 px-3 py-2">
                  <p className="text-sm text-ink">{name(id)}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(detail.satisfied?.[id] || []).map((s, i) => (
                      <span key={i} className="text-[10px] rounded bg-emerald/10 text-emerald px-1.5 py-0.5">{s.requirement} ✓</span>
                    ))}
                  </div>
                </div>
              ))}
              {(detail.eligible_ids || []).length === 0 && <p className="text-xs text-signal">None — {(detail.unmet_summary || []).join("; ")}</p>}
            </div>

            <p className="label-mono text-signal mb-2">Excluded ({detail.excluded?.length || 0})</p>
            <div className="space-y-1.5">
              {(detail.excluded || []).map((x) => (
                <div key={x.candidate_id} className="rounded-lg border border-white/10 bg-base/40 px-3 py-2">
                  <p className="text-sm text-ink">{name(x.candidate_id)}</p>
                  <div className="mt-1 space-y-0.5">
                    {x.unmet.map((u, i) => <p key={i} className="text-[11px] text-signal">✕ {u.reason}</p>)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
